import { FormikProps, useFormik } from 'formik'
import {
  DatePicker,
  LabraToolTip,
  labraUIDateFormat,
  ModalConfirm,
  ModalSizeVariants,
  PlanType,
} from '@labrav/react-components'
import { FormField } from '@labrav/react-components/lib/@types/types/forms'
import { DateTime } from 'luxon'
import React, { useCallback, useMemo, useState } from 'react'
import { useAuth } from '@labrav/react-components'
import { providerToAuthCloud, CloudAccountProvider } from '@labrav/common'
import {
  LoadingTypes,
  isLoading,
  useCurrentCloudProvider,
  expirationDateValidationErrorMsg,
  subscriptionEndDateValidationErrorMsg,
} from '@labrav/common'

import { useDispatch, useSelector } from '../../../store'
import { updatePrivateOfferForUser } from '../../../modules/privateOffer/action'
import { AddUpdateButton } from '../../../common/components/AddUpdateButton'
import UpdateIcon from '@mui/icons-material/Update'
import * as yup from 'yup'
import { isEmpty } from 'lodash'
import { useStyles } from './UpdatePrivateOfferExpiration.styles'
import {
  OfferStatus,
  OfferTypeRenew,
  Subscription,
} from '../../../modules/privateOffer/types'
import { isOfferExpired } from '../../../common/utils/isOfferExpired'
import {
  allowedPlanTypesForUpdateExpiryDate,
  updateOfferExpiringToolTipMessage,
} from '../../../utils/constants'

export type UpdatePrivateOfferExpirationProps = {
  offerExpirationDate: string
  offerSubscriptionEndDate: string
  offerId: string
  planType: PlanType
  offerStatus?: OfferStatus
  isDraft?: boolean
  aggrementStartDate?: string | null
  type?: OfferTypeRenew
  labraSubscriptionId?: string | null
  subscription?: Subscription
  partnerId?: string
  cloudMarketplace?: string
}
export const UpdatePrivateOfferExpiration = React.memo(
  ({
    offerExpirationDate,
    offerId,
    planType,
    offerSubscriptionEndDate,
    offerStatus,
    isDraft,
    aggrementStartDate,
    type,
    labraSubscriptionId,
    subscription,
    partnerId,
    cloudMarketplace: cloudMarketplaceProp,
  }: UpdatePrivateOfferExpirationProps) => {
    const { classes } = useStyles()
    const dispatch = useDispatch()
    const selectedCloudType = useCurrentCloudProvider()
    const expirationDate = DateTime.fromISO(offerExpirationDate)
    const expirationDateString = expirationDate.toISODate()
    const subscriptionEndDate = DateTime.fromISO(offerSubscriptionEndDate)
    const subscriptionEndDateString = subscriptionEndDate.toISODate()
    const formattedAgreementStartDate = aggrementStartDate
      ? DateTime.fromISO(aggrementStartDate).toFormat('dd-MM-yyyy')
      : undefined
    const formattedExpectedExpirationDate = subscription?.expectedExpirationDate
      ? DateTime.fromISO(subscription?.expectedExpirationDate).toFormat(
          'dd-MM-yyyy'
        )
      : undefined

    const loading = useSelector<boolean>(state =>
      isLoading(state.loading)(LoadingTypes.GENERAL)
    )
    const { loading: authLoading, canDo } = useAuth()
    const cloud = useCurrentCloudProvider()

    const canWriteMarketplace = useMemo(() => {
      return (
        !authLoading &&
        canDo({
          cloud: providerToAuthCloud(
            cloud.toLowerCase() as CloudAccountProvider
          ),
          service: 'marketplace',
          action: 'create',
        })
      )
    }, [authLoading, canDo, cloud])

    const permissionMessageString =
      'This action is disabled because you have read-only access'
    const [openExpiryDateModal, setExpiryDateModal] = useState<boolean>(false)
    const handleOpenModal = useCallback(() => {
      setExpiryDateModal(true)
    }, [openExpiryDateModal])
    const handleClose = useCallback(() => {
      formik.resetForm()
      setExpiryDateModal(false)
    }, [openExpiryDateModal])

    const isAWSRenewalOffer =
      type === OfferTypeRenew.RENEWAL && !isEmpty(labraSubscriptionId)

    const fields: FormField[] = [
      {
        title: 'Offer expiration date',
        humanReadableKey: 'Offer expiration date',
        placeHolder: 'Offer expiration date',
        id: 'offerExpirationDate',
        isRequired: true,
        isReadOnly: false,
        isCustom: false,
        defaultValue: expirationDateString || undefined,
        dataType: 'date',
        minDate: 'today',
      },
    ]
    const initialValue: Record<string, unknown> = {
      offerExpirationDate: expirationDateString,
      subscriptionEndDate: subscriptionEndDateString,
    }

    const validateSchema = yup.object().shape({
      offerExpirationDate: yup
        .string()
        .required(expirationDateValidationErrorMsg)
        .typeError(expirationDateValidationErrorMsg)
        .test('update-expiration-date', function (value) {
          if (aggrementStartDate) {
            const agreementStart = DateTime.fromISO(aggrementStartDate)
            const expirationDate = DateTime.fromISO(value || '')
            if (!(expirationDate < agreementStart)) {
              return this.createError({
                message: `Offer Expiration Date should be smaller than Specified Start Date (${formattedAgreementStartDate}).`,
              })
            }
          }
          if (isAWSRenewalOffer) {
            const expirationDate = DateTime.fromISO(value || '')
            const expectedExpirationDate = DateTime.fromISO(
              subscription?.expectedExpirationDate || ''
            )
            const offerSubscriptionEndDate = DateTime.fromISO(
              this.parent.subscriptionEndDate || ''
            )
            const comparisonDate =
              expectedExpirationDate < offerSubscriptionEndDate
                ? expectedExpirationDate
                : offerSubscriptionEndDate

            const formattedComparisonDate =
              comparisonDate.toFormat('dd-MM-yyyy')
            const message = comparisonDate.equals(expectedExpirationDate)
              ? `Offer Expiration Date should be smaller than or equal to the previous service end date (${formattedComparisonDate}).`
              : `Offer Expiration Date should be smaller than or equal to the service end date (${formattedComparisonDate}).`
            if (!(expirationDate <= comparisonDate)) {
              return this.createError({
                message: message,
              })
            }
          }
          return true
        }),
      subscriptionEndDate: yup
        .string()
        .nullable()
        .test(
          'check offer subscription end date',
          subscriptionEndDateValidationErrorMsg,
          function (value?: string | null) {
            if (!isEmpty(value)) {
              if (isAWSRenewalOffer || aggrementStartDate) {
                return true
              }
              return (
                DateTime.fromISO(this.parent.offerExpirationDate) <
                DateTime.fromISO(value || '')
              )
            }
            return true
          }
        ),
    })

    const noOps = () => void 0
    const formik = useFormik({
      initialValues: initialValue,
      validationSchema: validateSchema,
      validateOnBlur: true,
      validateOnChange: true,
      onSubmit: () => {
        // onSubmit is required in formik and here, this functionality doesn't need one.
        /* istanbul ignore next */
        noOps()
      },
    })
    const finalFields: FormField[] = useMemo(() => {
      if (
        planType === PlanType.SAAS_SUBSCRIPTION &&
        selectedCloudType === 'AWS'
      ) {
        fields.push({
          title: 'Subscription end date',
          humanReadableKey: 'Subscription end date',
          placeHolder: 'Subscription end date',
          id: 'subscriptionEndDate',
          isRequired: true,
          isReadOnly: false,
          isCustom: false,
          defaultValue: subscriptionEndDateString || undefined,
          dataType: 'date',
          minDate: DateTime.fromISO(
            formik.values.offerExpirationDate as string,
            { zone: 'UTC' }
          )
            .plus({ day: 1 })
            .toString(),
        })
      }
      return [...fields]
    }, [planType, formik.values.offerExpirationDate])
    const disabled = useMemo(() => {
      return (
        !isEmpty(formik.errors.offerExpirationDate) ||
        !isEmpty(formik.errors?.subscriptionEndDate || '')
      )
    }, [formik.errors.offerExpirationDate, formik.errors?.subscriptionEndDate])
    const handleAccept = useCallback(async () => {
      setExpiryDateModal(false)
      const dataToBeSent: Record<string, unknown> = {
        offerExpirationDate:
          selectedCloudType !== 'GCP'
            ? DateTime.fromISO(
                formik.values.offerExpirationDate as string
              ).toISODate()
            : DateTime.fromISO(
                formik.values.offerExpirationDate as string
              ).toISO(),
      }
      if (
        planType === PlanType.SAAS_SUBSCRIPTION &&
        selectedCloudType === 'AWS'
      ) {
        dataToBeSent['subscriptionEndDate'] = DateTime.fromISO(
          formik.values.subscriptionEndDate as string
        ).toISODate()
      }
      if (selectedCloudType === 'GCP') {
        dataToBeSent['offerStatus'] = offerStatus
      }
      await dispatch(
        updatePrivateOfferForUser(
          offerId,
          { ...dataToBeSent },
          undefined,
          false,
          undefined,
          (cloudMarketplaceProp || selectedCloudType) as never,
          partnerId
        )
      )
    }, [
      formik.values.offerExpirationDate,
      formik.values.subscriptionEndDate,
      planType,
    ])
    const isExpired = isOfferExpired(new Date(offerExpirationDate))

    const updateOfferStatusCheck =
      (offerStatus === OfferStatus?.PRIVATE_OFFER_CREATION_IN_PROGRESS &&
        !isDraft) ||
      offerStatus === OfferStatus?.PRIVATE_OFFER_FAILED ||
      offerStatus === OfferStatus.PRIVATE_OFFER_RESTRCITED ||
      offerStatus === OfferStatus.PRIVATE_OFFER_RESTRICTION_IN_PROGRESS ||
      (isExpired && !allowedPlanTypesForUpdateExpiryDate.includes(planType))

    return (
      <div className={classes.updateButtonContainer}>
        {!canWriteMarketplace ? (
          <LabraToolTip tooltipPlacement="top" label={permissionMessageString}>
            <AddUpdateButton
              icon={UpdateIcon}
              label={'Update offer expiration date'}
              disabled={true}
              type={'update'}
            />
          </LabraToolTip>
        ) : updateOfferStatusCheck ? (
          <LabraToolTip
            tooltipPlacement="top"
            label={updateOfferExpiringToolTipMessage}
          >
            <AddUpdateButton
              icon={UpdateIcon}
              label={'Update offer expiration date'}
              disabled={true}
              type={'update'}
            />
          </LabraToolTip>
        ) : (
          <>
            <AddUpdateButton
              icon={UpdateIcon}
              label={'Update offer expiration date'}
              disabled={loading}
              onClick={handleOpenModal}
              type={'update'}
            />
            <ModalConfirm
              title={'Update offer expiration date'}
              subTitle={''}
              content={<ShowDateInput formik={formik} fields={finalFields} />}
              open={openExpiryDateModal}
              titleAcceptButton={'Update'}
              titleCancelButton={'Cancel'}
              onAccept={handleAccept}
              onDecline={handleClose}
              variant={ModalSizeVariants.xSmall}
              hideCancelButton={false}
              acceptDisabled={loading || disabled}
            />
          </>
        )}
      </div>
    )
  }
)
export type ShowDateInputProps = {
  fields: FormField[]
  formik: FormikProps<Record<string, unknown>>
}
export const ShowDateInput = React.memo(
  ({ fields, formik }: ShowDateInputProps) => {
    const { classes } = useStyles()
    return (
      <>
        {fields.map(field => (
          <div className={classes.dateField} key={`div-${field.id}`}>
            <DatePicker
              formik={formik}
              format={labraUIDateFormat}
              field={field}
              key={field.id}
            />
          </div>
        ))}
      </>
    )
  }
)
